import React, { useCallback, useRef } from 'react';
import util from '../../styles/utilities.module.css';
import styles from './CredentialingDashboard.module.css';
import BadgeImage from './BadgeImage';
import ImpactStoryActions from './ImpactStoryActions';
import ModuleCarousel from './ModuleCarousel';
import { isBadgeReadyForAutoCollection } from '../../utils/credentialingUtils';

const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Skeleton module tiles (kept minimal & layout-faithful)
const ModuleCarouselSkeleton = () => {
  const tiles = Array.from({ length: 2 });
  return (
    <div className={styles.moduleCarouselContainer} aria-hidden="true">
      <div className={styles.skeletonCarousel}>
        {tiles.map((_, idx) => (
          <div
            key={idx}
            className={`${styles.moduleCarouselTile} ${styles.skeletonTile}`}
            style={{ minWidth: '280px', maxWidth: '340px' }}
          >
            <div className={styles.moduleTileHeader}>
              <div
                className={styles.skeletonBar}
                style={{ width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, margin: '0' }}
              />
              <div className={styles.moduleTileContent}>
                <div
                  className={styles.skeletonBar}
                  style={{ height: '16px', width: '95%', margin: '0 0 var(--space-2) 0' }}
                />
                <div
                  className={styles.skeletonBar}
                  style={{ height: '14px', width: '85%', margin: 0 }}
                />
              </div>
            </div>
            <div
              className={styles.skeletonBar}
              style={{ height: '14px', width: '100%', margin: 'var(--space-3) 0 2px 0' }}
            />
            <div
              className={styles.skeletonBar}
              style={{ height: '14px', width: '95%', margin: '2px 0' }}
            />
            <div
              className={styles.skeletonBar}
              style={{ height: '14px', width: '80%', margin: '2px 0 var(--space-2) 0' }}
            />
            <div className={styles.moduleTileMeta}>
              <div
                className={styles.skeletonBar}
                style={{ height: '12px', width: '70px', borderRadius: 'var(--radius-pill)', margin: 0 }}
              />
              <div
                className={styles.skeletonBar}
                style={{ height: '12px', width: '90px', borderRadius: 'var(--radius-pill)', margin: 0 }}
              />
            </div>
            <div className={styles.moduleTileAction}>
              <div
                className={styles.skeletonBar}
                style={{ height: '44px', width: '100%', borderRadius: 'var(--radius-lg)', marginTop: 'auto', paddingTop: 'var(--space-3)' }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BadgeProgressCard = ({
  skeleton = false,
  badgeData,
  overallProgress,
  moduleProgress,
  autoStatus,
  highlight = false,
  onViewBadge,
  onModuleClick,
  onRegisterRefs,
}) => {
  const cardNodeRef = useRef(null);
  const shareButtonNodeRef = useRef(null);

  const handleCardRef = useCallback((node) => {
    cardNodeRef.current = node;
    if (onRegisterRefs) {
      onRegisterRefs({ card: node, shareButton: shareButtonNodeRef.current });
    }
  }, [onRegisterRefs]);

  const handleShareButtonRef = useCallback((node) => {
    shareButtonNodeRef.current = node;
    if (onRegisterRefs) {
      onRegisterRefs({ card: cardNodeRef.current, shareButton: node });
    }
  }, [onRegisterRefs]);

  if (skeleton) {
    return (
      <div className={styles.progressCard} aria-hidden="true">
        <div className={styles.badgeCardGrid}>
          <div className={styles.badgeLeftColumn}>
            <div className={styles.skeletonBar} style={{ height: '26px', width: '95%', margin: '0 0 var(--space-4) 0' }} />
            <div className={styles.skeletonBar} style={{ height: '14px', width: '100%', margin: '0 0 4px 0' }} />
            <div className={styles.skeletonBar} style={{ height: '14px', width: '90%', margin: '0 0 var(--space-4) 0' }} />
            <div className={styles.badgeArtworkLoading} />
            <div className={styles.skeletonBar} style={{ height: '44px', width: '140px', alignSelf: 'center', marginTop: 'var(--space-2)', borderRadius: 'var(--radius-lg)' }} />
          </div>
          <div className={styles.badgeRightColumn}>
            <div className={styles.progressSection}>
              <div className={styles.skeletonBar} style={{ height: '16px', width: '160px', margin: '0 0 var(--space-3) 0' }} />
              <div className={styles.progressBarContainer}>
                <div className={styles.progressBarWrapper}>
                  <div className={styles.skeletonBar} style={{ height: '16px', width: '100%', borderRadius: 'var(--radius-pill)', margin: 0 }} />
                  <div className={styles.skeletonBar} style={{ height: '24px', width: '52px', borderRadius: 'var(--radius-md)', margin: 0 }} />
                </div>
              </div>
            </div>
            <ModuleCarouselSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!badgeData) return null; // safety

  const badgeId = badgeData.badge.id;
  const isHighlighted = highlight;
  const progressCardClass = combineClasses(
    styles.progressCard,
    autoStatus === 'error' ? styles.progressCardError : '',
    autoStatus === 'pending' ? styles.progressCardPending : '',
    isHighlighted ? styles.progressCardHighlight : ''
  );

  return (
    <div
      className={progressCardClass}
      ref={handleCardRef}
      data-testid="credentialing-badge-card"
      data-badge-id={badgeId}
    >
      <div className={styles.badgeCardGrid}>
        <div className={styles.badgeLeftColumn}>
          <h2 className={styles.badgeName}>{badgeData.badge.name}</h2>
          <p className={styles.badgeDescription}>{badgeData.badge.description}</p>
          <BadgeImage
            badgeId={badgeId}
            badgeName={badgeData.badge.name}
            badgeType={badgeData.badge.badge_type}
            isGhost={!badgeData.progress.is_earned}
          />
          {badgeData.progress.is_earned ? (
            badgeData.badge.open_badges_enabled === true ? (
              <button
                className={`${util.btn} ${util['btn--primaryGradient']} ${util.elevateHover} ${styles.shareButton} ${isHighlighted ? styles.badgeShareHighlight : ''}`}
                onClick={() => onViewBadge(badgeData)}
                ref={handleShareButtonRef}
              >
                📤 Share
              </button>
            ) : (
              <p className={styles.shareNote}>
                <small>Digital badge credentials are not available for this badge yet.</small>
              </p>
            )
          ) : isBadgeReadyForAutoCollection(badgeData) ? (
            autoStatus === 'error' ? (
              <div
                className={combineClasses(styles.collectionMessage, styles.collectionMessageError)}
                title="We had trouble issuing this badge automatically. Please contact the ClassGenius team so we can help."
              >
                <h3>⚠️ Needs Attention</h3>
                <p>
                  We couldn't finish issuing the {badgeData.badge.name} badge. Please reach out to the ClassGenius team so we can help.
                </p>
              </div>
            ) : (
              <div className={combineClasses(styles.collectionMessage, styles.collectionMessagePending)}>
                <h3>⏳ Finalizing your badge...</h3>
                <p>We're issuing your credential now. This usually takes less than a minute.</p>
              </div>
            )
          ) : null}
        </div>

        <div className={styles.badgeRightColumn}>
          <div className={styles.progressSection}>
            <h4 className={combineClasses(styles.progressSectionTitle, styles.progressTitleWithTooltip)}>
              <span>🏆 Your Progress</span>
            </h4>
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBarWrapper}>
                <div className={styles.progressBarTrack}>
                  <div
                    className={combineClasses(styles.progressBarFill, styles.badgeProgressFill)}
                    style={{ width: `${overallProgress.percentage}%` }}
                  />
                </div>
                <div className={styles.progressPercentage}>{overallProgress.percentage}%</div>
              </div>
            </div>
          </div>

          {badgeData.modules && badgeData.modules.length > 0 && (
            <div className={styles.moduleCarouselContainer}>
              <ModuleCarousel
                modules={badgeData.modules}
                onModuleClick={onModuleClick}
                moduleProgress={moduleProgress}
                badgeId={badgeId}
              />
            </div>
          )}

          {badgeData.badge.requires_impact_stories && (
            <div className={combineClasses(styles.progressSection, styles.highlightSection, styles.highlightCompact)}>
              <ImpactStoryActions />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgeProgressCard;
